import { useMemo, useState } from 'react';
import { Stack, Typography, Theme, Box } from '@mui/material';
import { ChevronRight, InfoOutlined, Close as CloseIcon } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FormHeader } from '../FormHeader';
import { BackButton } from 'components';
import { MyDocuments } from './components/MyDocuments';
import { ClinicDocuments } from './components/ClinicDocuments';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './DocumentsSettings.module.scss';

type Props = {
  onBackToSettingsClick(): void;
};

type Tab = 'myDocuments' | 'clinicDocuments';

export function DocumentsSettings({ onBackToSettingsClick }: Props) {
  const { t } = useTranslation('settings');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [tab, setTab] = useState<Tab | null>(isMobile ? null : 'clinicDocuments');

  const [hideBanner, setHideBanner] = useState(() => {
    return localStorage.getItem('hideDocumentBanner') === 'true';
  });

  const clinicDocumentsLabel = isHipaaLink
    ? t('documentsSettings.clinicDocumentsUpper')
    : t('documentsSettings.sharedDocumentsUpper');
  const clinicDocumentsLabelSingular = isHipaaLink
    ? t('documentsSettings.clinicDocuments')
    : t('documentsSettings.sharedDocuments');

  const headerItems = useMemo(
    () => [
      {
        text: clinicDocumentsLabel,
        isSelected: tab === 'clinicDocuments',
        onClick: () => setTab('clinicDocuments'),
      },
      {
        text: t('documentsSettings.myDocumentsUpper'),
        isSelected: tab === 'myDocuments',
        onClick: () => setTab('myDocuments'),
      },
    ],
    [tab, clinicDocumentsLabel, t],
  );

  const getMobileHeader = (): string => {
    switch (tab) {
      case 'myDocuments':
        return t('documentsSettings.myDocumentsUpper');
      case 'clinicDocuments':
        return clinicDocumentsLabel;
      default:
        return t('documentsSettings.documentsUpper');
    }
  };

  const handleBack = (): void => setTab(null);

  const Banner = () =>
    !hideBanner && tab ? (
      <Stack className={styles.banner} direction='row' alignItems='flex-start' justifyContent='space-between'>
        <Stack className={styles.bannerContent} direction='row'>
          <Box className={styles.iconBox}>
            <InfoOutlined sx={{ color: '#fff', fontSize: 24 }} />
          </Box>

          <Box>
            <Typography variant='body1' sx={{ fontWeight: 600, mb: '2px' }}>
              {isHipaaLink
                ? t('documentsSettings.secureStorageClientDocuments')
                : t('documentsSettings.secureStorageSharedDocuments')}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 640 }}>
              {t('documentsSettings.bannerIntro')} <strong>{clinicDocumentsLabelSingular}</strong>{' '}
              {t('documentsSettings.bannerSharedToTeam')} <strong>{t('documentsSettings.myDocuments')}</strong>{' '}
              {t('documentsSettings.bannerPrivateToYou')}
            </Typography>
          </Box>
        </Stack>

        <CloseIcon
          onClick={() => {
            setHideBanner(true);
            localStorage.setItem('hideDocumentBanner', 'true');
          }}
          className={styles.closeIcon}
        />
      </Stack>
    ) : null;

  return (
    <>
      {!tab && isMobile && <BackButton text={t('documentsSettings.backToSettings')} onClick={onBackToSettingsClick} />}
      {!!tab && isMobile && <BackButton text={t('documentsSettings.backToDocuments')} onClick={handleBack} />}

      <div className={styles.container}>
        {isMobile && (
          <>
            <FormHeader items={[{ text: getMobileHeader() }]} />
            {!tab && (
              <>
                <Stack className={styles.navigationItem} onClick={() => setTab('clinicDocuments')}>
                  <Typography variant='body2'>{clinicDocumentsLabelSingular}</Typography>
                  <ChevronRight />
                </Stack>
                <Stack className={styles.navigationItem} onClick={() => setTab('myDocuments')}>
                  <Typography variant='body2'>{t('documentsSettings.myDocuments')}</Typography>
                  <ChevronRight />
                </Stack>
              </>
            )}

            {tab && <Banner />}
            {tab === 'myDocuments' && <MyDocuments />}
            {tab === 'clinicDocuments' && <ClinicDocuments />}
          </>
        )}

        {!isMobile && (
          <>
            <FormHeader items={headerItems} />
            <Banner />
            <div className={styles.main}>
              {tab === 'myDocuments' && <MyDocuments />}
              {tab === 'clinicDocuments' && <ClinicDocuments />}
            </div>
          </>
        )}
      </div>
    </>
  );
}
